<template>
  <div class="p-4">

    <!-- ============================================================ -->
    <!-- SECCIÓN 1: Cónyuge / Conviviente                             -->
    <!-- ============================================================ -->
    <div class="mb-4">
      <h6 class="text-muted mb-3">Cónyuge / Conviviente</h6>

      <template v-if="spouse">
        <div class="border rounded p-3">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="d-flex align-items-center mb-2">
                <div class="dep-avatar mr-3">{{ getInitials(spouse) }}</div>
                <div>
                  <strong>{{ spouse.fullName }}</strong>
                  <span class="badge badge-info ml-2">{{ getRelationshipLabel(spouse.relationshipType) }}</span>
                </div>
              </div>
              <div class="row text-muted small">
                <div class="col-md-3">
                  <span class="d-block">Documento</span>
                  <strong class="text-dark">{{ getDocTypeLabel(spouse.documentType) }} {{ spouse.documentNumber }}</strong>
                </div>
                <div class="col-md-3">
                  <span class="d-block">Fecha nacimiento</span>
                  <strong class="text-dark">{{ spouse.birthDate }} — {{ spouse.age }} años</strong>
                </div>
                <div class="col-md-3" v-if="spouse.gender">
                  <span class="d-block">Género</span>
                  <strong class="text-dark">{{ spouse.gender === 'M' ? 'Masculino' : 'Femenino' }}</strong>
                </div>
                <div class="col-md-3" v-if="spouse.notes">
                  <span class="d-block">Observaciones</span>
                  <strong class="text-dark">{{ spouse.notes }}</strong>
                </div>
              </div>
            </div>
            <div class="d-flex">
              <button type="button" class="button button-light button-sm mr-2" @click="openEdit(spouse)">Editar</button>
              <button type="button" class="button button-light button-sm text-danger" @click="confirmDeactivate(spouse)">Desactivar</button>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="text-muted d-flex align-items-center">
          <span class="mr-3">No hay cónyuge/conviviente registrado</span>
          <button type="button" class="button button-primary button-sm" @click="openCreate('CONYUGE')">
            <i class="ri-add-line mr-1"></i>Registrar cónyuge
          </button>
        </div>
      </template>
    </div>

    <hr />

    <!-- ============================================================ -->
    <!-- SECCIÓN 2: Hijos                                             -->
    <!-- ============================================================ -->
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="text-muted mb-0">
          Hijos
          <span v-if="children.length > 0" class="badge badge-secondary ml-1">{{ children.length }}</span>
        </h6>
        <button type="button" class="button button-primary button-sm" @click="openCreate('HIJO')">
          <i class="ri-add-line mr-1"></i>Agregar hijo
        </button>
      </div>

      <template v-if="children.length > 0">
        <div class="table-responsive">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th>DOCUMENTO</th>
                <th>NOMBRE COMPLETO</th>
                <th>EDAD</th>
                <th>CONSTANCIA ESTUDIOS</th>
                <th>ESTADO AF</th>
                <th class="text-center">OPCIONES</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="child in children" :key="child.id">
                <td>{{ getDocTypeLabel(child.documentType) }} {{ child.documentNumber }}</td>
                <td>{{ child.fullName }}</td>
                <td>{{ child.age }} años</td>
                <td>
                  <template v-if="!child.hasStudyCertificate">-</template>
                  <template v-else>
                    <span :class="getCertificateClass(child)">{{ getCertificateText(child) }}</span>
                  </template>
                </td>
                <td>
                  <span v-if="child.familyAllowanceStatus" :class="['badge', getFamilyStatusColor(child.familyAllowanceStatus)]">
                    {{ getFamilyStatusLabel(child.familyAllowanceStatus) }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="text-center">
                  <b-dropdown variant="link" no-caret toggle-class="p-0">
                    <template #button-content>
                      <i class="ri-more-2-fill text-dark"></i>
                    </template>
                    <b-dropdown-item @click="openEdit(child)">Editar</b-dropdown-item>
                    <b-dropdown-item @click="confirmDeactivate(child)">Desactivar</b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-else>
        <p class="text-muted">No hay hijos registrados</p>
      </template>
    </div>

    <!-- ============================================================ -->
    <!-- SECCIÓN 3: Resumen asignación familiar                       -->
    <!-- ============================================================ -->
    <div v-if="children.length > 0" class="mb-3">
      <div v-if="qualifiedCount > 0" class="alert alert-success py-2 small mb-0">
        <i class="ri-check-line mr-1"></i>
        <strong>Tiene derecho a asignación familiar: Sí</strong>
        ({{ qualifiedCount }} hijo(s) calificado(s) de {{ children.length }} registrado(s))
      </div>
      <div v-else class="alert alert-danger py-2 small mb-0">
        <i class="ri-close-line mr-1"></i>
        <strong>Tiene derecho a asignación familiar: No</strong>
        (ningún hijo menor de 18 ni con constancia vigente)
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL: Agregar / Editar dependiente                          -->
    <!-- ============================================================ -->
    <b-modal
      id="modal-dependent"
      :title="modalTitle"
      @ok="saveDependent"
      ok-title="Guardar"
      cancel-title="Cancelar"
      :ok-disabled="!canSaveModal"
      size="lg"
      no-close-on-backdrop
    >
      <div class="row">
        <!-- Tipo de relación (solo para cónyuge) -->
        <div class="col-md-6" v-if="modalType !== 'HIJO'">
          <div class="form-group">
            <label>Tipo de relación <span class="text-danger">*</span></label>
            <FormSelect
              :items="spouseTypeOptions"
              v-model="modalForm.relationshipType"
            />
          </div>
        </div>

        <!-- Tipo de documento -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Tipo de documento <span class="text-danger">*</span></label>
            <FormSelect
              :items="docTypeOptions"
              v-model="modalForm.documentType"
              defaultOption="Seleccione"
            />
          </div>
        </div>

        <!-- Número de documento -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Número de documento <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.documentNumber" class="custom-form-control" placeholder="Número de documento" />
          </div>
        </div>

        <!-- Nombres -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Nombres <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.firstName" class="custom-form-control" placeholder="Nombres" />
          </div>
        </div>

        <!-- Apellido paterno -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Apellido paterno <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.lastNamePaternal" class="custom-form-control" placeholder="Apellido paterno" />
          </div>
        </div>

        <!-- Apellido materno -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Apellido materno</label>
            <input type="text" v-model="modalForm.lastNameMaternal" class="custom-form-control" placeholder="Apellido materno" />
          </div>
        </div>

        <!-- Fecha de nacimiento -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Fecha de nacimiento <span class="text-danger">*</span></label>
            <FormDatepicker v-model="modalForm.birthDate" />
            <small v-if="modalAge !== null" class="text-muted">{{ modalAge }} años</small>
          </div>
        </div>

        <!-- Género -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Género</label>
            <FormSelect
              :items="genderOptions"
              v-model="modalForm.gender"
              defaultOption="Seleccione"
            />
          </div>
        </div>

        <!-- Campos extra para HIJO -->
        <template v-if="modalType === 'HIJO'">
          <!-- Constancia de estudios (solo si edad >= 18) -->
          <div class="col-md-6" v-if="modalAge !== null && modalAge >= 18">
            <div class="form-group">
              <label>
                <input type="checkbox" v-model="modalForm.hasStudyCertificate" class="mr-1" />
                Constancia de estudios
              </label>
            </div>
          </div>

          <!-- Vencimiento constancia -->
          <div class="col-md-6" v-if="modalForm.hasStudyCertificate && modalAge >= 18">
            <div class="form-group">
              <label>Vencimiento de constancia <span class="text-danger">*</span></label>
              <FormDatepicker v-model="modalForm.studyCertificateExpiry" />
            </div>
          </div>

          <!-- Discapacidad -->
          <div class="col-md-6">
            <div class="form-group">
              <label>
                <input type="checkbox" v-model="modalForm.hasDisability" class="mr-1" />
                Tiene discapacidad
              </label>
            </div>
          </div>
        </template>

        <!-- Observaciones -->
        <div class="col-md-12">
          <div class="form-group">
            <label>Observaciones</label>
            <b-form-textarea v-model="modalForm.notes" rows="2" placeholder="Observaciones (opcional)" />
          </div>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { Alert, FormSelect, FormDatepicker } from 'wize-admin'
import { EmployeeDependentService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'FamilyTab',
  components: { FormSelect, FormDatepicker },
  props: {
    employeeId: { type: Number, required: true },
  },
  data() {
    return {
      dependents: [],
      editingDependent: null,
      modalType: 'HIJO',
      modalForm: this.emptyForm(),
      spouseTypeOptions: [
        { key: 'CONYUGE',     label: 'Cónyuge' },
        { key: 'CONVIVIENTE', label: 'Conviviente' },
      ],
      docTypeOptions: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
      ],
      genderOptions: [
        { key: 'M', label: 'Masculino' },
        { key: 'F', label: 'Femenino' },
      ],
    }
  },
  computed: {
    spouse() {
      return this.dependents.find(d =>
        d.relationshipType === 'CONYUGE' || d.relationshipType === 'CONVIVIENTE'
      ) || null
    },
    children() {
      return this.dependents
        .filter(d => d.relationshipType === 'HIJO')
        .sort((a, b) => (a.age || 0) - (b.age || 0))
    },
    qualifiedCount() {
      return this.children.filter(c =>
        c.familyAllowanceStatus === 'CALIFICA' || c.familyAllowanceStatus === 'CONSTANCIA_POR_VENCER'
      ).length
    },
    modalTitle() {
      if (this.modalType === 'HIJO') {
        return this.editingDependent ? 'Editar hijo' : 'Registrar hijo'
      }
      return this.editingDependent ? 'Editar cónyuge / conviviente' : 'Registrar cónyuge / conviviente'
    },
    modalAge() {
      if (!this.modalForm.birthDate) return null
      const birth = new Date(this.modalForm.birthDate + 'T00:00:00')
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const m = today.getMonth() - birth.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
      return age >= 0 ? age : 0
    },
    canSaveModal() {
      const f = this.modalForm
      if (!f.documentType || !f.documentNumber || !f.firstName || !f.lastNamePaternal || !f.birthDate) return false
      if (this.modalType !== 'HIJO' && !f.relationshipType) return false
      if (this.modalType === 'HIJO' && f.hasStudyCertificate && this.modalAge >= 18 && !f.studyCertificateExpiry) return false
      return true
    },
  },
  watch: {
    'modalForm.hasStudyCertificate'(val) {
      if (!val) this.modalForm.studyCertificateExpiry = null
    },
  },
  methods: {
    emptyForm() {
      return {
        relationshipType: null,
        documentType: null,
        documentNumber: null,
        firstName: null,
        lastNamePaternal: null,
        lastNameMaternal: null,
        birthDate: null,
        gender: null,
        hasStudyCertificate: false,
        studyCertificateExpiry: null,
        hasDisability: false,
        notes: null,
      }
    },

    async loadDependents() {
      try {
        const resp = await EmployeeDependentService.getByEmployee(this.employeeId)
        this.dependents = resp.payload || []
      } catch (e) {
        console.error('Error loading dependents', e)
      }
    },

    openCreate(type) {
      this.editingDependent = null
      this.modalType = type
      this.modalForm = this.emptyForm()
      if (type === 'HIJO') {
        this.modalForm.relationshipType = 'HIJO'
      } else {
        this.modalForm.relationshipType = 'CONYUGE'
      }
      this.$bvModal.show('modal-dependent')
    },

    openEdit(dep) {
      this.editingDependent = dep
      this.modalType = dep.relationshipType === 'HIJO' ? 'HIJO' : 'CONYUGE'
      this.modalForm = {
        relationshipType: dep.relationshipType,
        documentType: dep.documentType,
        documentNumber: dep.documentNumber,
        firstName: dep.firstName,
        lastNamePaternal: dep.lastNamePaternal,
        lastNameMaternal: dep.lastNameMaternal,
        birthDate: dep.birthDate,
        gender: dep.gender,
        hasStudyCertificate: dep.hasStudyCertificate || false,
        studyCertificateExpiry: dep.studyCertificateExpiry,
        hasDisability: dep.hasDisability || false,
        notes: dep.notes,
      }
      this.$bvModal.show('modal-dependent')
    },

    async saveDependent(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.canSaveModal) return
      try {
        this.$store.dispatch('app/loading', true)
        const data = { ...this.modalForm }
        if (this.modalType === 'HIJO') {
          data.relationshipType = 'HIJO'
        }
        if (!data.hasStudyCertificate) {
          data.studyCertificateExpiry = null
        }
        if (this.editingDependent) {
          await EmployeeDependentService.update(this.employeeId, this.editingDependent.id, data)
          Alert.success('Dependiente actualizado correctamente')
        } else {
          await EmployeeDependentService.create(this.employeeId, data)
          Alert.success('Dependiente registrado correctamente')
        }
        this.$bvModal.hide('modal-dependent')
        await this.loadDependents()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar el dependiente')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async confirmDeactivate(dep) {
      const name = dep.fullName || dep.firstName
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `¿Está seguro que desea desactivar a ${name}?`,
        { title: 'Desactivar dependiente', okVariant: 'danger', okTitle: 'Desactivar', cancelTitle: 'Cancelar' }
      )
      if (!confirmed) return
      try {
        this.$store.dispatch('app/loading', true)
        await EmployeeDependentService.deactivate(this.employeeId, dep.id)
        Alert.success('Dependiente desactivado')
        await this.loadDependents()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al desactivar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    // --- Helpers de formato ---

    getInitials(dep) {
      const f = (dep.firstName || '').trim()
      const l = (dep.lastNamePaternal || '').trim()
      return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?'
    },

    getRelationshipLabel(type) {
      const found = Object.values(Constants.RelationshipType).find(r => r.key === type)
      return found ? found.label : type
    },

    getDocTypeLabel(type) {
      const found = Object.values(Constants.IdentifierType).find(t => String(t.key) === String(type))
      return found ? found.label : type
    },

    getFamilyStatusColor(status) {
      const found = Object.values(Constants.FamilyAllowanceStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },

    getFamilyStatusLabel(status) {
      const found = Object.values(Constants.FamilyAllowanceStatus).find(s => s.key === status)
      return found ? found.label : status
    },

    getCertificateText(child) {
      if (!child.studyCertificateExpiry) return 'Sin fecha'
      const expiry = new Date(child.studyCertificateExpiry + 'T00:00:00')
      const today = new Date()
      if (expiry < today) return 'Vencida'
      const diffDays = Math.round((expiry - today) / (1000 * 60 * 60 * 24))
      if (diffDays <= 30) return `Vence ${child.studyCertificateExpiry}`
      return `Hasta ${child.studyCertificateExpiry}`
    },

    getCertificateClass(child) {
      if (!child.studyCertificateExpiry) return 'text-muted'
      const expiry = new Date(child.studyCertificateExpiry + 'T00:00:00')
      const today = new Date()
      if (expiry < today) return 'text-danger font-weight-bold'
      const diffDays = Math.round((expiry - today) / (1000 * 60 * 60 * 24))
      if (diffDays <= 30) return 'text-warning font-weight-bold'
      return 'text-success'
    },
  },

  created() {
    this.loadDependents()
  },
}
</script>

<style scoped>
.dep-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4f46e5;
  color: white;
  font-size: 0.875rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
</style>
